import type { ChannelType, Institution } from '@faceup/utils'
import { isUUIDv7 } from '@faceup/utils'
import { createContext, type ReactNode, useContext, useState } from 'react'
import { getUserData, persistUserDataSlice } from '../utils/userDataStorage'
import { useClient } from './ClientProvider'

/**
 * The minimal credential needed to bootstrap a report source: enough to run the
 * init query and authenticate. On a URL cold load this stays null - the route
 * wrapper works straight off the URL shortId. It is written only on explicit
 * user action before navigation (SearchInstitution, QrScanner), and in the app
 * it is hydrated from / persisted to the USER_DATA blob.
 */
export type AccessKey = {
  institutionId: string
  reportSourceId: string
  type: Institution
  channelType: ChannelType
  shortLink: string
  password?: string
}

type AccessKeyContextProps = {
  accessKey: AccessKey | null
  setAccessKey: (accessKey: AccessKey) => void
  clearAccessKey: () => void
}

const AccessKeyContext = createContext<AccessKeyContextProps>({
  accessKey: null,
  setAccessKey: () => undefined,
  clearAccessKey: () => undefined,
})

// The persisted blob keeps the legacy field name (defaultFormId) for 1:1 storage
// compatibility with the old app; the in-memory access key uses reportSourceId.
const hydrate = (): AccessKey | null => {
  const institution = getUserData()?.institution
  // installs from before report source ids became UUIDs still hold the old id, which the API no
  // longer accepts; the user picks the institution again (search / QR code) and the key is rewritten
  if (!institution || !isUUIDv7(institution.defaultFormId)) {
    return null
  }
  return {
    institutionId: institution.institutionId,
    reportSourceId: institution.defaultFormId,
    type: institution.type as Institution,
    channelType: institution.channelType as ChannelType,
    shortLink: institution.shortLink,
    ...(institution.password ? { password: institution.password } : {}),
  }
}

type AccessKeyProviderProps = {
  children: ReactNode
}

export const AccessKeyProvider = ({ children }: AccessKeyProviderProps) => {
  const { client } = useClient()
  const isApp = client === 'app'

  const [accessKey, setAccessKeyState] = useState<AccessKey | null>(() =>
    isApp ? hydrate() : null
  )

  const setAccessKey = (key: AccessKey) => {
    setAccessKeyState(key)
    if (isApp) {
      persistUserDataSlice({
        institution: {
          institutionId: key.institutionId,
          defaultFormId: key.reportSourceId,
          type: key.type,
          channelType: key.channelType,
          shortLink: key.shortLink,
          ...(key.password ? { password: key.password } : {}),
        },
      })
    }
  }

  const clearAccessKey = () => {
    setAccessKeyState(null)
    if (isApp) {
      persistUserDataSlice({ institution: null })
    }
  }

  return (
    <AccessKeyContext.Provider value={{ accessKey, setAccessKey, clearAccessKey }}>
      {children}
    </AccessKeyContext.Provider>
  )
}

export const useAccessKey = () => useContext(AccessKeyContext)
